import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const DocumentGrid = ({ documents, onDocumentSelect, selectedDocument, onUpload }) => {
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('date');

  const getFileIcon = (type) => {
    const iconMap = {
      pdf: 'FileText',
      doc: 'FileText',
      docx: 'FileText',
      xls: 'Sheet',
      xlsx: 'Sheet',
      jpg: 'Image',
      jpeg: 'Image',
      png: 'Image',
      zip: 'Archive',
    };
    return iconMap?.[type?.toLowerCase()] || 'File';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024)?.toFixed(1) + ' KB';
    return (bytes / (1024 * 1024))?.toFixed(1) + ' MB';
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="h-full flex flex-col bg-card">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-foreground">Documents</h2>
          <span className="text-sm text-muted-foreground">
            {documents?.length} items
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Upload"
            iconPosition="left"
            onClick={onUpload}
          >
            Upload
          </Button>
          <div className="flex items-center gap-1 border border-border rounded-md p-1">
            <button
              className={`p-1.5 rounded ${
                viewMode === 'grid' ?'bg-primary/10 text-primary' :'text-muted-foreground hover:bg-accent/10'
              }`}
              onClick={() => setViewMode('grid')}
              aria-label="Grid view"
            >
              <Icon name="Grid3x3" size={16} />
            </button>
            <button
              className={`p-1.5 rounded ${
                viewMode === 'list' ?'bg-primary/10 text-primary' :'text-muted-foreground hover:bg-accent/10'
              }`}
              onClick={() => setViewMode('list')}
              aria-label="List view"
            >
              <Icon name="List" size={16} />
            </button>
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e?.target?.value)}
            className="px-3 py-1.5 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="date">Date</option>
            <option value="name">Name</option>
            <option value="size">Size</option>
            <option value="type">Type</option>
          </select>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto custom-scrollbar p-4">
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {documents?.map((doc) => (
              <div
                key={doc?.id}
                className={`group relative bg-background border rounded-lg p-4 cursor-pointer transition-all duration-150 hover:shadow-md ${
                  selectedDocument?.id === doc?.id
                    ? 'border-primary ring-2 ring-primary/20' :'border-border hover:border-accent'
                }`}
                onClick={() => onDocumentSelect(doc)}
              >
                <div className="flex flex-col items-center">
                  {doc?.thumbnail ? (
                    <div className="w-full h-32 mb-3 rounded-md overflow-hidden bg-muted">
                      <Image
                        src={doc?.thumbnail}
                        alt={doc?.thumbnailAlt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-full h-32 mb-3 rounded-md bg-muted flex items-center justify-center">
                      <Icon
                        name={getFileIcon(doc?.type)}
                        size={48}
                        color="var(--color-muted-foreground)"
                      />
                    </div>
                  )}
                  <div className="w-full">
                    <p className="text-sm font-medium text-foreground truncate mb-1">
                      {doc?.name}
                    </p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{formatFileSize(doc?.size)}</span>
                      <span>{doc?.type?.toUpperCase()}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDate(doc?.uploadDate)}
                    </p>
                  </div>
                </div>
                {doc?.version > 1 && (
                  <div className="absolute top-2 right-2 bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
                    v{doc?.version}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {documents?.map((doc) => (
              <div
                key={doc?.id}
                className={`flex items-center gap-4 p-3 rounded-lg cursor-pointer transition-all duration-150 ${
                  selectedDocument?.id === doc?.id
                    ? 'bg-primary/10 border border-primary' :'bg-background border border-border hover:bg-accent/10'
                }`}
                onClick={() => onDocumentSelect(doc)}
              >
                <Icon
                  name={getFileIcon(doc?.type)}
                  size={32}
                  color={
                    selectedDocument?.id === doc?.id
                      ? 'var(--color-primary)'
                      : 'var(--color-muted-foreground)'
                  }
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {doc?.name}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                    <span>{formatFileSize(doc?.size)}</span>
                    <span>{doc?.type?.toUpperCase()}</span>
                    <span>{formatDate(doc?.uploadDate)}</span>
                    <span>by {doc?.uploadedBy}</span>
                  </div>
                </div>
                {doc?.version > 1 && (
                  <div className="bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
                    v{doc?.version}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <button
                    className="p-1.5 rounded-md hover:bg-accent/10 transition-colors"
                    onClick={(e) => {
                      e?.stopPropagation();
                    }}
                    aria-label="Download document"
                  >
                    <Icon name="Download" size={16} color="var(--color-muted-foreground)" />
                  </button>
                  <button
                    className="p-1.5 rounded-md hover:bg-accent/10 transition-colors"
                    onClick={(e) => {
                      e?.stopPropagation();
                    }}
                    aria-label="More options"
                  >
                    <Icon name="MoreVertical" size={16} color="var(--color-muted-foreground)" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentGrid;